import { useEffect, useRef } from "react";
import axiosClient from "api";

// Safety net under the socket for an open chat.
//
// Live messages arrive over socket.io, but the backend runs as several
// processes (cluster workers, and more than one replica in Kubernetes), and a
// room broadcast only reaches sockets held by the same process unless an
// adapter relays it. The cluster adapter covers workers inside one replica;
// nothing covers two replicas. So while a chat is open and the tab is
// visible, ask the server every few seconds for messages newer than the
// newest one we have and merge any we missed. Worst case a message shows a
// few seconds late instead of only after a refresh.
export const useMessagePolling = (
  roomId,
  messages,
  setMessages,
  { intervalMs = 5000, enabled = true } = {}
) => {
  const lastIdRef = useRef(0);

  useEffect(() => {
    lastIdRef.current = (messages || []).reduce(
      (max, m) => Math.max(max, Number(m && m.id) || 0),
      0
    );
  }, [messages]);

  useEffect(() => {
    if (!roomId || !enabled) return undefined;
    let cancelled = false;
    let inFlight = false;

    const tick = async () => {
      if (inFlight || document.visibilityState !== "visible") return;
      inFlight = true;
      try {
        const res = await axiosClient.post("/api/chat/new-messages", {
          roomId,
          afterId: lastIdRef.current,
        });
        const fresh = (res && res.data && res.data.data && res.data.data.messages) || [];
        if (cancelled || fresh.length === 0) return;
        setMessages((prev) => {
          const seen = new Set((prev || []).map((m) => String(m.id)));
          const add = fresh.filter((m) => !seen.has(String(m.id)));
          return add.length ? [...(prev || []), ...add] : prev;
        });
      } catch (_) {
        // Transient; the next tick tries again.
      } finally {
        inFlight = false;
      }
    };

    const timer = setInterval(tick, intervalMs);
    const onVisible = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [roomId, enabled, intervalMs, setMessages]);
};
